package org.teddapp.project.ui.admin

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.decodeToImageBitmap
import org.teddapp.project.server.ServerSiteFile
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddVisibility
import org.teddapp.project.ui.TeddVisibilityBadge
import org.teddapp.project.ui.projects.MenuAction
import org.teddapp.project.ui.projects.ProjectMoreMenu
import org.teddapp.project.ui.teddClickable
import org.teddapp.project.ui.teddSurface

// ──────────────────────────────────────────────────────────────────────
// La galerie : toutes les images envoyées au serveur, en vignettes. Celles
// déposées depuis l'éditeur ou la galerie elle-même, et celles jointes aux
// notes et aux pièces de l'appareil. Une image envoyée une fois sert partout :
// couverture, image d'une pièce, image dans un texte.
// ──────────────────────────────────────────────────────────────────────

/** Une vignette : les octets viennent du serveur, décodés par Compose. */
@OptIn(ExperimentalResourceApi::class)
@Composable
private fun Thumb(model: AdminModel, f: ServerSiteFile, modifier: Modifier) {
    var bitmap by remember(f.id) { mutableStateOf<ImageBitmap?>(null) }
    var failed by remember(f.id) { mutableStateOf(false) }
    LaunchedEffect(f.id) {
        bitmap = withContext(Dispatchers.Default) { runCatching { model.galleryBytes(f)?.decodeToImageBitmap() }.getOrNull() }
        failed = bitmap == null
    }
    val b = bitmap
    Box(modifier.background(TeddTokens.SurfaceSunken), contentAlignment = Alignment.Center) {
        if (b != null) Image(bitmap = b, contentDescription = f.file_name, contentScale = ContentScale.Crop, modifier = Modifier.fillMaxWidth().aspectRatio(4f / 3f))
        else Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(TeddSpace.S1), modifier = Modifier.padding(TeddSpace.S3)) {
            TeddIcon(name = "image", size = 20.dp, color = TeddTokens.InkSubtle)
            Text(if (failed) "Pas d'aperçu" else "…", color = TeddTokens.InkSubtle, style = TeddType.Caption)
        }
    }
}

/** D'où vient une image, en un mot. */
private fun originOf(f: ServerSiteFile) = when (f.owner_kind) {
    "site" -> "déposée ici"
    "note" -> "jointe à une note"
    "portfolio_piece" -> "jointe à une pièce"
    else -> f.owner_kind
}

/** La grille des images, avec pour chacune son nom, son origine, où elle sert, et ses gestes. */
@Composable
internal fun GalleryGrid(model: AdminModel, compact: Boolean) {
    val files = model.files.orEmpty()
    val clipboard = LocalClipboardManager.current
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text("GALERIE · ${files.size}", color = TeddTokens.InkMuted, style = TeddType.Overline, modifier = Modifier.weight(1f))
            TeddButton(text = "Rafraîchir", onClick = { model.refreshFiles() }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "refresh")
        }
        Text(
            "Toutes tes images sur le serveur. Une image de la galerie se choisit depuis l'éditeur : couverture, image d'une pièce, image dans un texte. Le site sert celles qu'un contenu en ligne cite.",
            color = TeddTokens.InkMuted, style = TeddType.Caption,
        )
        if (files.isEmpty()) {
            TeddEmptyState(
                title = "La galerie est vide",
                message = if (TeddImagePicker.pick != null) "« Ajouter une image » dépose un fichier de cet appareil. Les images jointes à tes pièces arrivent aussi ici à la synchronisation."
                else "Cet appareil ne sait pas choisir une image : passe par l'ordinateur, Android ou le navigateur.",
                icon = "image",
                actionLabel = if (TeddImagePicker.pick != null) "Ajouter une image" else null,
                onAction = if (TeddImagePicker.pick != null) ({ model.addToGallery() }) else null,
            )
            return@Column
        }
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
            maxItemsInEachRow = if (compact) 2 else 4,
        ) {
            files.forEach { f ->
                TeddCard(modifier = Modifier.weight(1f), shape = RoundedCornerShape(TeddTokens.RMd), padding = PaddingValues(0.dp)) {
                    Column {
                        Thumb(model, f, Modifier.fillMaxWidth().clip(RoundedCornerShape(topStart = TeddTokens.RMd, topEnd = TeddTokens.RMd)))
                        Column(Modifier.padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                                Text(f.file_name, color = TeddTokens.Ink, style = TeddType.Label, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                                ProjectMoreMenu(
                                    listOfNotNull(
                                        MenuAction("Copier la référence", "copy", { clipboard.setText(AnnotatedString("file:${f.id}")); model.snackbar.show("Référence copiée : colle-la comme adresse d'image") }),
                                        if (f.owner_kind == "site") MenuAction("Supprimer", "trash", { model.deleteFromGallery(f) }, danger = true) else null,
                                    ),
                                    size = 28.dp,
                                )
                            }
                            Text(
                                listOf(formatBytes(f.size_bytes), originOf(f), formatDay(f.created_at_ms)).joinToString(" · "),
                                color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis,
                            )
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                                if (f.online) TeddVisibilityBadge(TeddVisibility.Public, label = "Sur le site")
                                else if (f.owner_title != null) TeddTag("Citée")
                                else TeddTag("Libre")
                                f.owner_title?.let { Text(it, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis) }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** La galerie en dialogue : on choisit une image, le geste qui l'a demandée reçoit `file:<id>`. */
@Composable
internal fun GalleryPickerDialog(model: AdminModel) {
    val onPick = model.galleryPicker ?: return
    val files = model.files.orEmpty()
    Dialog(onDismissRequest = { model.galleryPicker = null }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            Modifier.widthIn(max = 760.dp).fillMaxWidth().padding(TeddSpace.S4).heightIn(max = 720.dp).teddSurface(RoundedCornerShape(TeddTokens.RLg), elevation = 2.dp),
        ) {
            Row(Modifier.fillMaxWidth().padding(start = TeddSpace.S5, end = TeddSpace.S3, top = TeddSpace.S4, bottom = TeddSpace.S3), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                Text("Choisir dans la galerie", color = TeddTokens.Ink, style = TeddType.Title1, modifier = Modifier.weight(1f))
                if (TeddImagePicker.pick != null) TeddButton(text = "Ajouter une image", onClick = { model.addToGallery() }, size = TeddButtonSize.Sm, icon = "plus", enabled = !model.busy)
                TeddButton(text = "Fermer", onClick = { model.galleryPicker = null }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
            }
            Box(Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S5).padding(bottom = TeddSpace.S5).verticalScroll(rememberScrollState())) {
                if (files.isEmpty()) {
                    TeddEmptyState(title = "La galerie est vide", message = "Ajoute une image, ou envoie-en depuis une pièce de l'appli.", icon = "image")
                } else FlowRow(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3), verticalArrangement = Arrangement.spacedBy(TeddSpace.S3), maxItemsInEachRow = 4) {
                    files.forEach { f ->
                        Column(
                            Modifier.width(160.dp).teddClickable(onClick = { onPick("file:${f.id}"); model.galleryPicker = null }, shape = RoundedCornerShape(TeddTokens.RMd)),
                            verticalArrangement = Arrangement.spacedBy(TeddSpace.S1),
                        ) {
                            Thumb(model, f, Modifier.fillMaxWidth().clip(RoundedCornerShape(TeddTokens.RMd)))
                            Text(f.file_name, color = TeddTokens.Ink, style = TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.padding(horizontal = 4.dp))
                        }
                    }
                }
            }
        }
    }
}
